import { useCallback, useState } from 'react';

import TextInput from '../Forms/TextInput';
import List from '../List';

import { EditableMultiInputRowActions } from './EditableMultiInputRowActions';
import type { EditableMultiInputItem } from './EditableMultiInputContainer';

type Props<T extends EditableMultiInputItem> = {
  item: T;
  isEditing: boolean;
  index: number;
  toggleIsEditing: () => void;
  onDelete: () => void;
  onEdit: (newName: string) => void;
  hasEditPermission: boolean;
  maxItemNameLength: number;
  inputValidationRegex?: RegExp;
  onValidationChange?: (isValid: boolean) => void;
};

export const EditableMultiInputRow = <T extends EditableMultiInputItem>({
  item,
  isEditing,
  index,
  toggleIsEditing,
  onDelete,
  onEdit,
  hasEditPermission,
  maxItemNameLength,
  inputValidationRegex,
  onValidationChange,
}: Props<T>) => {
  const [editItemName, setEditItemName] = useState(item.name);
  const [isValid, setIsValid] = useState(true);

  const handleChange = (newName: string) => {
    setEditItemName(newName);

    if (inputValidationRegex && onValidationChange) {
      const isValidInput = inputValidationRegex.test(newName);
      setIsValid(isValidInput);
      onValidationChange(isValidInput);
    }
  };

  const onSave = useCallback(
    (newName: string) => {
      onEdit(newName);
      toggleIsEditing();
    },
    [toggleIsEditing, onEdit],
  );

  return (
    <List.Row
      key={`${item.id}-${item.name}`}
      right={
        <EditableMultiInputRowActions
          hasEditPermission={hasEditPermission}
          onDelete={onDelete}
          isEditing={isEditing}
          toggleIsEditing={toggleIsEditing}
          isSaveDisabled={item.name === editItemName || !isValid}
          onSave={() => onSave(editItemName)}
          isNew={item.isNew}
        />
      }
      hasTightVerticalPadding
      isInteractive={false}
      isHighlighted={(index + 1) % 2 === 0}
    >
      {isEditing ? (
        <TextInput
          size="small"
          value={editItemName}
          label="change item name"
          placeholder="Enter a unique name..."
          maxLength={maxItemNameLength}
          suffix={`${editItemName.length}/${maxItemNameLength}`}
          onChange={handleChange}
        />
      ) : (
        <div>{item.name}</div>
      )}
    </List.Row>
  );
};
